import GooglePayConfig from '@/configuration/sources/smartform/googlePay.yml'
import GooglePayManager from '@/ghost/service/GooglePayManager'
import paymentMethodsConf from '@/configuration/sources/smartform/paymentMethodsConf.yml'
import { getGooglePayFormattedAmount } from '@/common/util/currencyFormatter'
import { isInsecureEnvironment } from '@/common/util/environment'
import { logSentryException } from '@/common/util/sentry'
import { getAllowedCardNetworks } from '@/ghost/utils/googlePaymentData'

export const getGooglePayState = () => {
  return {
    googlePay: {
      ready: false,
      testMode: false,
      simulator: {
        open: false
      }
    }
  }
}

export const googlePayActions = ($locator, app) => {
  return {
    // Called from host
    async setupGooglePayHost({
      state: { isUnitaryTest },
      getters,
      commit,
      dispatch
    }) {
      if (!getters.isGooglePayCompatible) {
        dispatch('removePaymentMethod', 'GOOGLEPAY')
        dispatch('removeDnaPaymentMethod', 'GOOGLEPAY')
      } else {
        if (getters.isGooglePaySimulator) {
          if (isUnitaryTest) return

          const module = await import(
            /* webpackChunkName: "kr-simulator-google-pay" */ '@/simulator/googlepay/Loader'
          )
          const loader = module.default
          loader($locator)
          commit('UPDATE', { googlePay: { ready: true, testMode: true } })
        } else {
          const { allReadyQueue, storeFactory } = $locator
          // Need to setup googlePay from the ghost see KJS-4025
          allReadyQueue.send(storeFactory.create('setupGooglePaySDK'))
        }
      }
    },
    // Called from ghost
    async setupGooglePaySDK({
      state: {
        testKeys,
        isUnitaryTest,
        dna,
        smartForm: { dnaCardBrands }
      },
      getters,
      commit,
      dispatch
    }) {
      if (!getters.isGooglePaySimulator) {
        if (isInsecureEnvironment() || isUnitaryTest) return

        await $locator.scriptLoader.loadScriptPromise(
          paymentMethodsConf.sources.GOOGLEPAY,
          'kr-google-pay'
        )
        const {
          askCvv,
          walletShippingAddress,
          cards: dnaMetadataCards,
          CBEnabled
        } = dna.smartForm?.GOOGLEPAY?.metadata || {}

        const isReadyResult = await GooglePayManager.isReadyToPayCall({
          testMode: testKeys,
          cvcRequired: !!askCvv,
          dnaBrands: dnaCardBrands,
          dnaMetadataCards,
          shippingAddressRequired: !!walletShippingAddress,
          disableVisaOnWallet: dna.disableVisaOnWallet,
          cbEnabled: !!CBEnabled
        })?.catch(error => ({ error, result: false }))

        if (!isReadyResult?.result) {
          dispatch('removePaymentMethod', 'GOOGLEPAY')
          dispatch('removeDnaPaymentMethod', 'GOOGLEPAY')

          logSentryException(isReadyResult.error, 'store/modules/googlePay')
          return
        }

        commit('UPDATE', { googlePay: { ready: true, testMode: testKeys } })
      }
    },
    startGooglePayPayment({ state, dispatch, commit, getters }) {
      if (isInsecureEnvironment() && !getters.isGooglePaySimulator) {
        dispatch('error', {
          errorCode: 'CLIENT_514',
          paymentMethod: 'GOOGLEPAY'
        })
        return
      }

      if (!state.googlePay.ready) return

      dispatch('cleanError')
      commit('UPDATE', {
        smartForm: { activeMethod: 'GOOGLEPAY', activeMethodMetadata: null }
      })

      const {
        gateway,
        gatewayMerchantId,
        askCvv,
        walletShippingAddress,
        cards: dnaMetadataCards,
        CBEnabled
      } = state.dna.smartForm?.GOOGLEPAY?.metadata || {}
      const { domain, protocol } = state.merchant
      const merchantOrigin = `${protocol}//${domain}`
      const amount = state.dna?.equivalentValue ?? state.amount
      const currency = state.dna?.equivalentValueCurrency ?? state.currency

      $locator.googlePayManager.start({
        merchantName: state.dna.shopName,
        merchantId: GooglePayConfig.merchantId,
        merchantOrigin,
        gateway,
        gatewayMerchantId,
        cvcRequired: !!askCvv,
        countryCode: state.country,
        currencyCode: currency,
        totalPrice: getGooglePayFormattedAmount(amount, currency),
        dnaBrands: state.smartForm.dnaCardBrands,
        dnaMetadataCards,
        testMode: state.googlePay.testMode,
        testModeWithoutSimulator: getters.googlePayTestModeWithoutSimulator,
        shippingAddressRequired: !!walletShippingAddress,
        disableVisaOnWallet: state.dna.disableVisaOnWallet,
        brandPriority: state.dna.brandPriority,
        ...(CBEnabled && {
          cbEnabled: true
        })
      })
    },
    openGooglePaySimulator({ commit }) {
      commit('UPDATE', {
        googlePay: { simulator: { open: true } }
      })
    },
    closeGooglePaySimulator({ commit }) {
      commit('UPDATE', {
        googlePay: { simulator: { open: false } }
      })
    }
  }
}
export const googlePayMutations = {}
export const googlePayGetters = {
  googlePayTestModeWithoutSimulator: (_state, { isSandboxMail }) => {
    return isSandboxMail
  },
  isGooglePaySimulator: (
    { testKeys },
    { googlePayTestModeWithoutSimulator }
  ) => {
    return testKeys && !googlePayTestModeWithoutSimulator
  },
  isGooglePaySimulatorOpen: ({ googlePay: { simulator } }) => simulator.open,
  isGooglePayActive: (_state, { isActiveMethod }) =>
    isActiveMethod('GOOGLEPAY'),
  isGooglePayCompatible: (
    { smartForm: { dnaCardBrands }, dna },
    { isFirefox }
  ) => {
    const googlePayMetadata = dna?.smartForm?.GOOGLEPAY?.metadata
    const dnaMetadataCards = googlePayMetadata?.cards
    const allowedCardBrands = getAllowedCardNetworks({
      dnaBrands: dnaCardBrands,
      dnaMetadataCards,
      cbEnabled: !!googlePayMetadata?.CBEnabled
    })
    return !isFirefox && !!allowedCardBrands.length
  }
}
